import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { importExcel } from "@/excel";
import { useFilteredIds } from "@/hooks/useFilteredIds";
import { useTranslations } from "@/queries/useTranslations";
import type { IState } from "@/state";
import { editContent, type IDispatch } from "@/state";
import { buildExportData } from "@/translationFiltering";
import { type IContentType, type ILanguage, LANGUAGES } from "@/types";

function parseKey(fullKey: string): [IContentType, string] {
    const dotIndex = fullKey.indexOf(".");
    if (dotIndex === -1) {
        throw new Error(`Invalid key: ${fullKey}`);
    }
    let contentType = fullKey.substring(0, dotIndex);
    const id = fullKey.substring(dotIndex + 1);
    if (contentType === "default-content") {
        contentType = "defaultContent";
    }
    return [contentType as IContentType, id];
}

interface IProps {
    close: VoidFunction;
}

function ImportModal({ close }: IProps) {
    const dispatch = useDispatch<IDispatch>();
    const { version } = useParams({ from: "/$version" });
    const [lang, setlang] = useState<ILanguage>("en");
    const { data: translations } = useTranslations(version);
    const editsByContent = useSelector((state: IState) => state.edits[version]?.data);
    const { filtered } = useFilteredIds(version);
    const referenceData =
        translations && filtered ? buildExportData({ translations, editsByContent, filtered, lang }) : {};

    const handleImport = async (file: File) => {
        try {
            // Parse XLSX file to get imported data
            const importedData = await importExcel(file);

            // Filter to keep only keys where the imported value differs from referenceData
            const changes = Object.entries(importedData).reduce(
                (acc, [fullKey, newValue]) => {
                    if (referenceData[fullKey] !== newValue) {
                        acc[fullKey] = newValue;
                    }
                    return acc;
                },
                {} as Record<string, string>,
            );

            // Dispatch an edit for each change
            for (const [fullKey, newValue] of Object.entries(changes)) {
                const [contentType, id] = parseKey(fullKey);

                dispatch(
                    editContent({
                        version,
                        contentType,
                        lang,
                        id,
                        value: newValue,
                    }),
                );
            }
            close();
        } catch (error) {
            console.error("Error importing Excel file:", error);
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        await handleImport(file);
        e.target.value = "";
    };

    return (
        <div>
            <h2>Import answers for {version} as xlsx</h2>
            <div>
                <label>
                    Select langauge to import:{" "}
                    <select value={lang} onChange={(e) => setlang(e.target.value as ILanguage)}>
                        {LANGUAGES.map((l) => (
                            <option key={l} value={l}>
                                {l}
                            </option>
                        ))}
                    </select>
                </label>
                <input
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleFileChange}
                />
            </div>
        </div>
    );
}

export default ImportModal;
